import { EditOutlined } from "@ant-design/icons";
import { Button, notification, Space, Table, Tooltip, DatePicker, Col, Form, Row } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { SlideDown } from "react-slidedown";
import "react-slidedown/lib/slidedown.css";
import { coreApi } from "../../api/calls";
import CleaningClaimStatus from "./CleaningClaimStatus";
import CleaningEditModal from "./CleaningEditModal";
import MassClaimModal from "./MassClaimModal";
import MassPromoModal from "./MassPromoModal";
import MassSubstituteModal from "./MassSubstituteModal";
import BuildingUnitLink from "../../components/Buildings/BuildingUnitLink";
import PartnerLink from "../Partners/PartnerLink";

/**
 * Cleaning List Complex
 * @component
 * @alias CleaningListComplex
 * @property {object} partner
 * @property {object} building
 * @property {object} buildingUnit
 * @returns <React.Fragment /> containing <Table /> that has all the cleanings with ability to filter data
 */
const CleaningListComplex = ({ partner = null, building = null, buildingUnit = null, showMassClaimButton = true }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const cancelTokenRef = useRef(null);

  const [pagination, setPagination] = useState({});

  const [dateFrom, setDateFrom] = useState(moment().startOf("month"));
  const [dateTo, setDateTo] = useState(moment().endOf("month"));

  const [filterForm] = Form.useForm();

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedCleaning, setSelectedCleaning] = useState();
  const [cleaning, setCleaning] = useState();

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [cleanings, setCleanings] = useState([]);
  const [units, setUnits] = useState([]);

  const refreshCleanings = useCallback(() => {

    // Cancel the previous request if it exists
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Canceled due to new request");
    }

    // Create a new cancellation token for the current request
    cancelTokenRef.current = axios.CancelToken.source();

    let params = {
      partner_id: partner ? partner.id : null,
      building_id: building ? building.id : null,
      building_unit_id: buildingUnit ? buildingUnit?.id : null,
      date_from: dateFrom ? dateFrom.format("YYYY-MM-DD") : null,
      date_to: dateTo ? dateTo.format("YYYY-MM-DD") : null,
    };

    setLoading(true);

    axios
      .all([
        coreApi.get("/cleanings/recurring", {
          params: params,
          cancelToken: cancelTokenRef.current.token,
        }),
        coreApi.get("/cleanings/one-time", {
          params: params,
          cancelToken: cancelTokenRef.current.token,
        }),
      ])
      .then(
        axios.spread((recurringCleaningsResponse, oneTimeCleaningsResponse) => {
          let { ...pagination } = recurringCleaningsResponse.data;

          setPagination(pagination);
          setCleanings([...recurringCleaningsResponse.data, ...oneTimeCleaningsResponse.data]);
        })
      )
      .catch((error) => {
        if (!axios.isCancel(error) && error.response) {
          notification.error({ message: error?.response?.data?.message });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [partner, building, buildingUnit, dateFrom, dateTo]);

  useEffect(() => {
    refreshCleanings();

    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("Cleanup function to cancel the request when unmounting a component");
      }
    };
  }, [refreshCleanings]);

  useEffect(() => {
    let buildingUnits = [];

    cleanings.forEach((cleaning) => {
      if (!buildingUnits.find((unit) => unit.value === cleaning?.building_unit?.id)) {
        buildingUnits.push({
          text: cleaning?.building_unit?.street + " " + cleaning?.building_unit?.house_number,
          value: cleaning?.building_unit?.id,
        });
      }
    });

    setUnits(buildingUnits);
  }, [cleanings]);

  // Update selectedCleaning when cleanings list changes (after refresh)
  useEffect(() => {
    if (selectedCleaning?.cleaning_id && cleanings.length > 0) {
      const updatedCleaning = cleanings.find((c) => c.cleaning_id === selectedCleaning.cleaning_id);
      if (updatedCleaning && JSON.stringify(updatedCleaning) !== JSON.stringify(selectedCleaning)) {
        setSelectedCleaning(updatedCleaning);
      }
    }
  }, [cleanings, selectedCleaning]);

  const changeCleaning = (direction, cleaning_id) => {
    const index = cleanings.findIndex((i) => i.cleaning_id === cleaning_id);
    if (cleanings[index + direction]) {
      setSelectedCleaning(cleanings[index + direction]);
    }
  };

  // Columns of table
  const tableColumns = [
    {
      title: t("cleanings.dateExpected"),
      key: "date_expected",
      dataIndex: "date_expected",
      sorter: (a, b) => moment(a.date_expected).unix() - moment(b.date_expected).unix(),
      render: (date) => moment(date).format("L"),
    },
    {
      title: t("schedules.buildingUnit"),
      key: "building_unit",
      sorter: (a, b) => a.building_unit.street.localeCompare(b.building_unit.street),
      render: (cleaning) => (
        <BuildingUnitLink buildingUnit={cleaning?.building_unit}/>
      ),
      filters: units,
      filterMultiple: true,
      onFilter: (value, record) => record?.building_unit?.id === value,
    },
    {
      title: t("cleanings.fullName"),
      key: "company_name",
      sorter: (a, b) =>
        a.partner?.last_name
          ? a.partner?.last_name.localeCompare(b.partner?.last_name)
          : a.partner?.company_name.localeCompare(b.partner?.company_name),
      render: (cleaning) => (
        <PartnerLink partner={cleaning?.partner} />
      ),
    },
    {
      title: t("common.city"),
      sorter: (a, b) => a.building_unit.building.city_branch.name.localeCompare(b.building_unit.building.city_branch.name),
      render: (data) => data.building_unit.building.city_branch.name,
    },
    {
      title: t("cleanings.substitute"),
      key: "substitute",
      sorter: (a, b) =>
        a.substitute?.last_name
          ? a.substitute?.last_name.localeCompare(b.substitute?.last_name)
          : a.substitute?.company_name.localeCompare(b.substitute?.company_name),
      render: (cleaning) => {
        return (
          cleaning?.substitute_id && (
            <Tooltip
              title={
                cleaning?.substitute?.first_name + " " + cleaning?.substitute?.last_name ||
                cleaning?.substitute?.company_name
              }
            >
              <Link to={"/partners/" + cleaning?.substitute_id} style={{ borderBottom: "1px dotted" }}>
                {cleaning?.substitute?.first_name + " " + cleaning?.substitute?.last_name ||
                  cleaning?.substitute?.company_name}
              </Link>
            </Tooltip>
          )
        );
      },
    },
    {
      title: t("cleanings.price"),
      key: "price",
      sorter: (a, b) => a.price - b.price,
      render: (cleaning) => {
        return new Intl.NumberFormat("cs-CZ", {
          style: "currency",
          currency: cleaning.currency_iso_4217 || "CZK",
        }).format(cleaning.price);
      },
    },
    {
      title: t("cleanings.promo"),
      key: "promo",
      sorter: (a, b) => a.promo - b.promo,
      render: (cleaning) => {
        return new Intl.NumberFormat("cs-CZ", {
          style: "currency",
          currency: cleaning.currency_iso_4217 || "CZK",
        }).format(cleaning.promo);
      },
    },
    {
      title: t("cleanings.partnerProfit"),
      key: "partner_profit",
      sorter: (a, b) => a.partner_profit - b.partner_profit,
      render: (cleaning) => {
        return new Intl.NumberFormat("cs-CZ", {
          style: "currency",
          currency: cleaning.currency_iso_4217 || "CZK",
        }).format(cleaning.partner_profit);
      },
    },
    {
      title: t("cleanings.annaProfit"),
      key: "anna_profit",
      sorter: (a, b) => a.anna_profit - b.anna_profit,
      render: (cleaning) => {
        return new Intl.NumberFormat("cs-CZ", {
          style: "currency",
          currency: cleaning.currency_iso_4217 || "CZK",
        }).format(cleaning.anna_profit);
      },
    },
    {
      title: t("cleanings.substituteProfit"),
      key: "substitute_profit",
      sorter: (a, b) => a.substitute_profit - b.substitute_profit,
      render: (cleaning) => {
        return new Intl.NumberFormat("cs-CZ", {
          style: "currency",
          currency: cleaning.currency_iso_4217 || "CZK",
        }).format(cleaning.substitute_profit);
      },
    },
    {
      title: t("cleanings.claimStatus"),
      key: "claim_status",
      render: (cleaning) => <CleaningClaimStatus cleaning={cleaning} />,
      filters: [
        {
          text: t("cleanings.claimInSolution"),
          value: "is_claim_in_solution",
        },
        {
          text: t("cleanings.claimSolved"),
          value: "is_claim_solved",
        },
      ],
      filterMultiple: true,
      onFilter: (value, record) => {
        return Boolean(record[value]) === true;
      },
    },
    {
      title: t("cleanings.serviceFee.title"),
      key: "service_fee",
      width: "95px",
      render: (cleaning) => {
        if (cleaning?.anna_partner_line_item) {
          const lineItem = cleaning.anna_partner_line_item;
          const amount = lineItem.total_amount || 0;
          const impact = lineItem.service_fee_impact;

          if (impact === "increase" || impact === "decrease") {
            let color = impact === "increase" ? "#B92F46" : "#4CAF50";
            
            return (
              <span style={{ color, fontWeight: "bold" }}>
                {new Intl.NumberFormat("cs-CZ", {
                  style: "currency",
                  currency: cleaning.currency_iso_4217 || "CZK",
                }).format(amount)}
              </span>
            );
          }
        }
        return "—";
      },
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 50,
      render: (cleaning) => (
        <Button
          size={"small"}
          icon={<EditOutlined />}
          onClick={() => {
            setSelectedCleaning(cleaning);
            setEditModalOpen(true);
          }}
        ></Button>
      ),
    },
  ];

  return (
    <React.Fragment>
      <SlideDown className={"my-dropdown-slidedown"} closed={selectedRows.length < 1}>
        {/* Multiple-buttons */}
        <Space style={{ paddingBottom: "16px" }}>
          <MassSubstituteModal
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            partnerId={partner?.id}
            refresh={refreshCleanings}
          />

          <MassPromoModal
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            partnerId={partner?.id}
            refresh={refreshCleanings}
            cleaning={cleaning}
          />

          {showMassClaimButton && (
            <MassClaimModal
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              partnerId={partner?.id}
              refresh={refreshCleanings}
              cleaning={cleaning}
            />
          )}

          {/* <Button
            icon={<CalendarOutlined />}
            size={"small"}
            onClick={() => {
              console.log("dateRealChange");
              setSelectedMultipleActionType(4);
              setMultipleEditModalOpen(true);
            }}
          >
            {t("cleanings.dateRealChange")}
          </Button> */}
        </Space>
      </SlideDown>
      {/* Select range of dates */}
      <Row>
        <Col span={24}>
          <Form
            layout={"inline"}
            form={filterForm}
            className="filterForm"
            style={{ dispay: "flex", gap: "12px", paddingBottom: "20px" }}
          >
            <Col span={9}>
              <Form.Item label={t("common.selectedPeriod")} name={"period"} initialValue={[dateFrom, dateTo]}>
                <DatePicker.RangePicker
                  allowClear={false}
                  allowEmpty={false}
                  onChange={(value) => {
                    setDateFrom(value[0] || moment().startOf("month"));
                    setDateTo(value[1] || moment().endOf("month"));
                  }}
                  style={{ width: "100%" }}
                ></DatePicker.RangePicker>
              </Form.Item>
            </Col>
            {/* Dates reset button */}
            <Form.Item>
              <Link
                to="#"
                onClick={() => {
                  filterForm.resetFields();
                  setDateFrom(null);
                  setDateTo(null);
                }}
              >
                {t("common.resetFilters")}
              </Link>
            </Form.Item>
          </Form>
        </Col>
      </Row>
      <Table
        size={"small"}
        rowKey={"id"}
        loading={loading}
        pagination={{
          ...pagination,
          showTotal: (total, range) => (
            <p>
              {t("common.paginationPartOne")} <strong>{range[0]}</strong>-<strong>{range[1]}</strong>
              {" " + t("common.paginationPartTwo") + " "}
              <strong>{total}</strong> {t("common.paginationPartThree")}
            </p>
          ),
          showSizeChanger: true,
        }}
        rowSelection={{
          selectedRowKeys: selectedRows,
          selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT],
          onChange: (selectedRowKeys, element) => {
            setSelectedRows(selectedRowKeys);
            if (element) setCleaning(element[0]);
          },
        }}
        columns={tableColumns}
        dataSource={cleanings}
      />

      <CleaningEditModal
        cleaning={selectedCleaning}
        setCleaning={setSelectedCleaning}
        isOpen={isEditModalOpen}
        close={() => {
          setEditModalOpen(false);
        }}
        changeCleaning={changeCleaning}
        refreshParent={refreshCleanings}
      />
    </React.Fragment>
  );
};

export default CleaningListComplex;
